import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider } from '@arco-design/web-react';
// Arco 编译期主题入口（less 源码 + token 覆盖，见 styles/arco-theme.less）
import './styles/arco-theme.less';
// 全局样式按域拆分（import 顺序即 CSS 级联顺序，后置文件覆盖前置，勿随意调整）
import './styles/tokens.css';
import './styles/layout.css';
import './styles/components.css';
import './styles/pages/monitor.css';
import './styles/pages/ivr.css';
import './styles/pages/voc.css';
import './styles/pages/skill-groups.css';
import './styles/pages/auth.css';
import App from './App';
import PhoneMockGate from './dev/PhoneMockGate';
// 主题在 React 渲染前应用：避免先渲染默认配色再跳到用户所选主题（首屏闪变）
import { initTheme } from './theme/applyTheme';

initTheme();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <ConfigProvider
    componentConfig={{
      // 关闭 Modal/Drawer 默认自聚焦（Arco autoFocus 默认 true）：避免打开
      // 弹窗/抽屉时光标自动落在首个输入框，触发浏览器自动补全/历史记录下拉
      Modal: { autoFocus: false },
      // 抽屉内的弹层（Select/Dropdown/DatePicker/Tooltip…）默认被挂进 .arco-drawer-content，
      // 而该节点是 position:relative + overflow:auto —— 它同时是弹层的包含块，弹层会被裁到
      // 正文可视区内（下拉只露一条、贴边的问号说明被切），且随正文一起滚走。
      // getChildrenPopupContainer 是 Arco 官方逃生口：弹层改挂 body（ConfigProvider 已注入
      // 抽屉层级 +1 的 z-index，仍在抽屉之上）；Arco 仍会监听正文的滚动事件重算位置，
      // 所以「跟随滚动」不丢。单点收敛，避免 30+ 个抽屉各自补 getPopupContainer。
      Drawer: { autoFocus: false, getChildrenPopupContainer: () => document.body },
      // 默认按钮语言统一：Arco 无 type 按钮默认落到 secondary 灰底，
      // 与禁用态视觉混淆。经 ConfigProvider 设默认 type='outline'（白底描边可点），
      // 渲染类变为 arco-btn-outline；显式 type（text/primary/danger/outline 等）优先级更高不受影响。
      // outline 禁用态灰化见 styles/components.css
      Button: { type: 'outline' },
      // 下拉菜单默认点击触发：Arco Dropdown 自身默认 hover，并写死 400ms 悬浮等待，且点击既打不开
      // 也收不起（键盘/触屏也够不到）。此默认档只为兜住将来新写的 Dropdown 不再回落到 hover；
      // 各调用点仍显式声明 trigger（意图就地可见，不依赖全局）。Arco 内部显式传 trigger 的浮层
      // （折叠侧栏分组、Tabs 溢出、表格筛选、Dropdown.Button）取值优先，不受影响。
      Dropdown: { trigger: 'click' },
      // 下拉列表可视高度统一为 320px（Arco 默认 200px ≈ 5 项）：组织/场景/技能组这类长枚举
      // 默认高度下要反复滚动才能找到目标。走官方 props（dropdownMenuStyle 落到
      // .arco-select-popup-inner 的内联样式，盖过 Arco 的 max-height: 200px），
      // 不新增 .arco-* 全局覆盖；短枚举由 max-height 封顶、无视觉影响。
      // 单个 Select 自行传 dropdownMenuStyle 时以自身为准。
      Select: { dropdownMenuStyle: { maxHeight: 320 } },
      // 弹层跟随滚动（全站单点）：页面滚动发生在 `.bcc-content`（不是 window），而 Arco 的 Trigger
      // 缺省 `updateOnScroll: false`——弹层挂 body 后位置只在打开那一刻算一次，滚页面时下拉/气泡会与
      // 触发控件脱开（长表单页最明显：滚动后下拉停在原地、盖住别的字段）。
      // Trigger 是 Select/DatePicker/Dropdown/Tooltip/Cascader… 的共同底座，且它把
      // `componentConfig.Trigger` 合并进自身默认 props，故这一处即覆盖全站；某个弹层要保留"钉住"行为
      // 可单点显式传 `updateOnScroll: false`。
      Trigger: { updateOnScroll: true },
      // 表格行 hover 默认关闭：Arco 默认 hover=true，悬停会给整行铺一层 5% 主色底，
      // 而操作列的文字按钮自身 hover 已是 8% 主色 pill（styles/components.css 操作列统一档）——
      // 两层同格叠加显脏（固定列是实体底色，叠加后更明显）。
      // 走官方 props 关全局默认，ProTable/ProPage 与抽屉/弹窗内小表口径一致；
      // 单个表格仍可显式传 hover 打开（显式 props 优先于 componentConfig）。
      // 行可点击与选中态分别用 cursor:pointer、.bcc-row-sel 表达，不依赖行 hover 底色。
      Table: { hover: false },
    }}
  >
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <App />
      {/* DEV 电话仿真（仅 ?mock=1 生效，生产构建为空渲染）：见 dev/PhoneMockGate.tsx */}
      <PhoneMockGate />
    </BrowserRouter>
  </ConfigProvider>
);
